import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useApp } from '../contexts/AppContext';
import { Send, Plus, MessageSquare, Bot, User } from 'lucide-react';
import toast from 'react-hot-toast';

const Chat = () => {
  const {
    chatSessions,
    currentSession,
    setCurrentSession,
    createChatSession,
    sendMessage,
    getChatSession // <-- import from context
  } = useApp();

  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [telegramCode, setTelegramCode] = useState('');
  const [telegramStatus, setTelegramStatus] = useState({ linked: false });
  const [loadingTelegram, setLoadingTelegram] = useState(false);
  const messagesEndRef = useRef(null);

  const telegramBotLink = import.meta.env.VITE_TELEGRAM_BOT_LINK || '';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const visibleMessages = messages.filter((m) => m.role !== 'system');

  // Fetch messages when currentSession changes
  useEffect(() => {
    const fetchMessages = async () => {
      if (currentSession) {
        try {
          const session = await getChatSession(currentSession.id);
          setMessages(session.messages || []);
        } catch (error) {
          setMessages([]);
        }
      } else {
        setMessages([]);
      }
    };
    fetchMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSession]);

  const loadTelegramStatus = async () => {
    try {
      const res = await axios.get('/api/integrations/telegram/link/status');
      setTelegramStatus(res.data || { linked: false });
    } catch {
      setTelegramStatus({ linked: false });
    }
  };

  useEffect(() => {
    loadTelegramStatus();
  }, []);

  const verifyTelegramCode = async () => {
    const code = telegramCode.trim();
    if (!code) {
      toast.error('Enter the Telegram code first');
      return;
    }

    try {
      setLoadingTelegram(true);
      await axios.post('/api/integrations/telegram/link/verify', { code });
      toast.success('Telegram linked successfully');
      setTelegramCode('');
      loadTelegramStatus();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to link Telegram');
    } finally {
      setLoadingTelegram(false);
    }
  };

  const unlinkTelegram = async () => {
    if (!window.confirm('Unlink Telegram from this account?')) return;
    try {
      await axios.delete('/api/integrations/telegram/link');
      toast.success('Telegram unlinked');
      loadTelegramStatus();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to unlink Telegram');
    }
  };

  const handleCreateSession = async () => {
    try {
      const session = await createChatSession('New Chat Session');
      setCurrentSession(session);
      // setMessages([]); // No need, useEffect will fetch
    } catch (error) {
      toast.error('Failed to create chat session');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || !currentSession || loading) return;

    const userMessage = message.trim();
    setMessage('');
    setLoading(true);

    try {
      // Send message to backend
      console.log('🚀 Sending message to backend:', userMessage);
      const response = await sendMessage(currentSession.id, userMessage);
      console.log('✅ Backend response:', response);
      
      // Fetch the updated session with all messages including the new assistant response
      console.log('🔄 Refreshing chat session messages...');
      const updatedSession = await getChatSession(currentSession.id);
      console.log('📥 Updated session data:', updatedSession);
      
      // Update the messages state with the complete message history
      setMessages(updatedSession.messages || []);
      console.log('✅ Messages updated in UI');
      
    } catch (error) {
      console.error('❌ Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 h-[calc(100vh-200px)]">
        {/* Chat Sessions Sidebar */}
        <div className="lg:col-span-1 card p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Chat Sessions</h2>
            <button
              onClick={handleCreateSession}
              className="p-2 text-purple-600 hover:bg-purple-100 rounded-lg transition-colors duration-200"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>

          <div className="mb-4 p-3 rounded-lg border border-gray-200 bg-gray-50">
            <p className="text-sm font-medium text-gray-900">Telegram Integration</p>
            <p className="text-xs text-gray-600 mt-1">
              Get a code from the bot with <code>/link</code>, then paste it below.
            </p>
            {telegramBotLink && (
              <a
                href={telegramBotLink}
                target="_blank"
                rel="noreferrer"
                className="inline-block mt-2 text-xs text-purple-700 hover:text-purple-800"
              >
                Open Telegram bot ↗
              </a>
            )}

            <div className="mt-2 flex gap-2">
              <input
                value={telegramCode}
                onChange={(e) => setTelegramCode(e.target.value)}
                placeholder="6-digit code"
                className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded-md"
              />
              <button
                onClick={verifyTelegramCode}
                disabled={loadingTelegram}
                className="px-2 py-1 text-xs rounded-md bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-60"
              >
                {loadingTelegram ? 'Linking...' : 'Link'}
              </button>
            </div>

            <p className={`mt-2 text-xs ${telegramStatus?.linked ? 'text-green-700' : 'text-yellow-700'}`}>
              {telegramStatus?.linked
                ? `Linked ${telegramStatus.telegram_username ? `as @${telegramStatus.telegram_username}` : ''}`
                : 'Telegram not linked yet'}
            </p>
            {telegramStatus?.linked && (
              <button
                onClick={unlinkTelegram}
                className="mt-2 text-xs text-red-600 hover:text-red-700"
              >
                Unlink Telegram
              </button>
            )}
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto">
            {chatSessions.map((session) => (
              <button
                key={session.id}
                onClick={async () => {
                  setCurrentSession(session);
                  // Fetch messages for the selected session
                  try {
                    const s = await getChatSession(session.id);
                    setMessages(s.messages || []);
                  } catch {
                    setMessages([]);
                  }
                }}
                className={`w-full text-left p-3 rounded-lg transition-colors duration-200 ${
                  currentSession?.id === session.id
                    ? 'bg-purple-100 text-purple-700'
                    : 'hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <MessageSquare className="h-4 w-4" />
                  <span className="truncate text-sm">
                    {session.title || 'Untitled Chat'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {new Date(session.created_at).toLocaleDateString()}
                </p>
              </button>
            ))}
          </div>

          {chatSessions.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-sm">No chat sessions yet</p>
              <button
                onClick={handleCreateSession}
                className="btn-primary mt-3 text-sm"
              >
                Start First Chat
              </button>
            </div>
          )}
        </div>

        {/* Chat Area */}
        <div className="lg:col-span-3 card p-0 flex flex-col">
          {currentSession ? (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {currentSession.title || 'Chat Session'}
                </h3>
                <p className="text-sm text-gray-500">
                  Ask questions about your documents
                </p>
              </div>

              {/* Messages */}
              <div className="flex-1 p-4 overflow-y-auto space-y-4">
                {visibleMessages.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <Bot className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Ready to help!
                    </h3>
                    <p>Ask me anything about your uploaded documents.</p>
                  </div>
                )}

                {visibleMessages.map((msg, index) => {
                  return (
                    <div
                      key={msg.id || index}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                          msg.role === 'user'
                            ? 'bg-purple-600 text-white'
                            : msg.role === 'assistant'
                            ? 'bg-gray-100 text-gray-900'
                            : 'bg-blue-50 text-blue-900'
                        }`}
                      >
                        <div className="flex items-center space-x-2 mb-1">
                          {msg.role === 'user' ? (
                            <User className="h-4 w-4" />
                          ) : msg.role === 'assistant' ? (
                            <Bot className="h-4 w-4" />
                          ) : (
                            <MessageSquare className="h-4 w-4" />
                          )}
                          <span className="text-xs opacity-75">
                            {msg.role === 'user' ? 'You' : msg.role === 'assistant' ? 'Lumina' : 'System'}
                          </span>
                          <span className="text-xs opacity-75">
                            {new Date(msg.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>

                        {msg.sources && msg.sources.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-300">
                            <p className="text-xs opacity-75 mb-1">Sources:</p>
                            {msg.sources.map((source, sourceIndex) => (
                              <p key={sourceIndex} className="text-xs opacity-75">
                                • {source.filename || 'Document'}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-gray-100 text-gray-900 max-w-xs lg:max-w-md px-4 py-2 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <Bot className="h-4 w-4" />
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div className="p-4 border-t border-gray-200">
                <form onSubmit={handleSendMessage} className="flex space-x-3">
                  <input
                    type="text"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Ask a question about your documents..."
                    className="flex-1 input-field"
                    disabled={loading}
                  />
                  <button
                    type="submit"
                    disabled={!message.trim() || loading}
                    className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </form>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <MessageSquare className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Select a chat session
                </h3>
                <p>Choose an existing session or create a new one to start chatting.</p>
                <button
                  onClick={handleCreateSession}
                  className="btn-primary mt-4"
                >
                  New Chat Session
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Chat;
